import { ReviewsTypes } from '@frontend/api-reviews';
import { FacebookLogo, GoogleLogo } from '@frontend/assets';
import { useTranslation } from '@frontend/i18n';
import { ReviewRating, WeaveIcon } from '@frontend/reviews';
import type { GetWeavePopNotificationByType, NotificationActions } from '@frontend/types';
import { theme } from '@frontend/theme';
import { styles, Text } from '@frontend/design-system';
import { BaseNotificationComponentProps, BaseNotificationInner, BaseNotificationOuter } from './base';

type Props = BaseNotificationComponentProps<GetWeavePopNotificationByType<'review-new'>>;

export const ReviewNotificationComponent = ({ isHistorical, notification, emit, ...rest }: Props) => {
  const { t } = useTranslation();
  const actions: NotificationActions = [
    {
      label: t('View'),
      action: () => {
        emit({ action: 'view', payload: undefined }, notification);
      },
    },
  ];

  const getSiteName = (siteNameString: string) => {
    switch (siteNameString.toLowerCase()) {
      case 'google':
        return ReviewsTypes.SiteName.Google;
      case 'facebook':
        return ReviewsTypes.SiteName.Facebook;
      case 'private':
        return ReviewsTypes.SiteName.Private;
      default:
        return undefined;
    }
  };

  const siteName = getSiteName(notification.payload.siteName);

  if (!siteName) return null;

  return (
    <BaseNotificationOuter
      actions={actions}
      notification={notification}
      emit={emit}
      isHistorical={isHistorical}
      {...rest}
    >
      <BaseNotificationInner
        onClose={() => emit({ action: 'ignore', payload: undefined }, notification)}
        actions={actions}
        title={notification.payload.title}
        body={
          <div
            css={{
              display: 'flex',
              alignItems: 'start',
              gap: theme.spacing(1),
            }}
          >
            {notification.payload.siteName === 'google' ? (
              <GoogleLogo css={{ minWidth: 'fit-content', marginTop: theme.spacing(0.5) }} />
            ) : notification.payload.siteName === 'facebook' ? (
              <FacebookLogo css={{ minWidth: 'fit-content', marginTop: theme.spacing(0.5) }} />
            ) : notification.payload.siteName === 'private' ? (
              <WeaveIcon css={{ marginTop: theme.spacing(0.5) }} />
            ) : null}
            <div
              css={{
                display: 'flex',
                flexDirection: 'column',
              }}
            >
              <Text weight='bold'>{notification.payload.author}</Text>
              <ReviewRating
                siteName={siteName}
                rating={notification.payload.rating}
                recommendation={
                  notification.payload.recommendationType === 'positive'
                    ? ReviewsTypes.Recommendation.Recommended
                    : ReviewsTypes.Recommendation.NotRecommended
                }
              />

              <Text css={styles.multilineTruncate(2)}>{notification.payload.body}</Text>
            </div>
          </div>
        }
        location={notification.location}
        timestamp={notification.timestamp}
        id={notification.id}
        notificationType={notification.type}
        {...rest}
      />
    </BaseNotificationOuter>
  );
};
